import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, from, of } from 'rxjs';

import { DocumentLayoutsApiService } from '../../../shared/shared-services/document-layouts/document-layouts-api.service';
import { selectSelectedStore } from '../../user-access/user-stores-store/user-store.selectors';
import { SettingsReferenceEnum } from '../models';
import { documentLayoutActions } from './document-layout.actions';
import { settingsSetSelected } from './setting.actions';

@Injectable()
export class DocumentLayoutEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(DocumentLayoutsApiService);
  private readonly store = inject(Store);

  /**
   * Opening Purchase Order Layout starts the same loads the page shows a spinner for.
   * Clicking the item again also reloads, so the spinner cannot stay up after the page is already open.
   */
  loadOnOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsSetSelected.setSelectedReference),
      filter(({ ref }) => ref === SettingsReferenceEnum.featureAutoOrderDocumentLayouts),
      withLatestFrom(this.store.select(selectSelectedStore)),
      switchMap(([, store]) => {
        const storeId = store?.storeId;
        if (!storeId) {
          return EMPTY;
        }
        return of(
          documentLayoutActions.loadLayouts(),
          documentLayoutActions.loadSettings({ storeId }),
        );
      }),
    ),
  );

  loadLayouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(documentLayoutActions.loadLayouts),
      switchMap(() =>
        this.api.listPurchaseOrderLayouts().pipe(
          map((layouts) => documentLayoutActions.loadLayoutsSuccess({ layouts })),
          catchError(() =>
            of(
              documentLayoutActions.loadLayoutsFailure({
                message: 'Could not load purchase order layouts.',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(documentLayoutActions.loadSettings),
      switchMap(({ storeId }) =>
        this.api.getSettings(storeId).pipe(
          map((settings) => documentLayoutActions.loadSettingsSuccess({ storeId, settings })),
          catchError(() =>
            of(
              documentLayoutActions.loadSettingsFailure({
                message: 'Could not load document layout settings.',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  saveSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(documentLayoutActions.saveSettings),
      switchMap(({ storeId, settings }) =>
        this.api.saveSettings(storeId, settings).pipe(
          map((saved) => documentLayoutActions.saveSettingsSuccess({ storeId, settings: saved })),
          catchError(() =>
            of(
              documentLayoutActions.saveSettingsFailure({
                message: 'Could not save document layout settings.',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  preview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(documentLayoutActions.preview),
      switchMap(({ accentColour, layoutKey, storeId }) =>
        this.api.previewPurchaseOrder(storeId, layoutKey, accentColour).pipe(
          switchMap((blob) =>
            from(blob.slice(0, 5).text()).pipe(
              map((header) => {
                if (!header.startsWith('%PDF')) {
                  return documentLayoutActions.previewFailure({
                    message: 'Preview was not a PDF.',
                  });
                }
                return documentLayoutActions.previewSuccess({ url: URL.createObjectURL(blob) });
              }),
            ),
          ),
          catchError(() =>
            of(
              documentLayoutActions.previewFailure({
                message: 'Could not render the purchase order preview.',
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
